import type React from "react";
import { useCallback, useDeferredValue, useMemo, useState } from "react";

import { Combobox, IconButton } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import {
    getVisibleNestedGroups,
    isRegionExpandedWhenSearching,
} from "app/pages/sales-intelligence/common-components/dropdown/LocationDropdown/utils";
import type { ContactsCountry } from "app/pages/sales-intelligence/modules/company-analysis/types/contacts";

import { EmployeeLocationRegionSection } from "./components/EmployeeLocationRegionSection";
import CountryDropdownButton from "./CountryDropdownButton/CountryDropdownButton";
import DropdownHeader from "./DropdownHeader/DropdownHeader";
import { useEmployeeLocationGroups } from "./hooks/useEmployeeLocationGroups";
import type { ContactsCountryGroup } from "./utils/employeeLocationGroups";

const DATA_AUTOMATION = "employee-location-dropdown";

type EmployeeLocationDropdownProps = {
    onSelect: (ids: readonly number[]) => void;
    onOpen?: () => void;
    isDisabled: boolean;
    countries?: readonly ContactsCountry[];
    selectedIds: readonly number[];
    customButtonText?: string;
};

const EmployeeLocationDropdown = (props: EmployeeLocationDropdownProps) => {
    const {
        countries,
        selectedIds,
        isDisabled,
        onSelect,
        onOpen,
        customButtonText = "si.account-overview.employee-location.dropdown.button-text",
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [expanded, setExpanded] = useState(() => new Set<string>());
    const deferredSearchTerm = useDeferredValue(searchTerm);

    const groups = useEmployeeLocationGroups(countries ?? []);

    const visibleGroups = useMemo(
        () => getVisibleNestedGroups<ContactsCountry, ContactsCountryGroup>(groups, deferredSearchTerm),
        [groups, deferredSearchTerm],
    );

    const selectedCodes = useMemo(() => new Set(selectedIds), [selectedIds]);

    const searchActive = deferredSearchTerm.trim().length > 0;

    const isRegionExpanded = useCallback(
        (regionName: string) => isRegionExpandedWhenSearching(expanded, searchActive, regionName),
        [expanded, searchActive],
    );

    const buttonText = useMemo(() => {
        if (selectedIds.length === 0) {
            return translate(customButtonText);
        }

        const [first, ...rest] = selectedIds;
        const countryObject = countries?.find((country) => country.id === first);

        if (rest.length === 0) {
            return `${countryObject?.text ?? "Unknown country"}`;
        }

        return `${countryObject?.text ?? ""} +${rest.length}`;
    }, [countries, customButtonText, selectedIds]);

    const getCountriesNames = useCallback(
        (ids: readonly number[]) =>
            ids
                .reduce<string[]>((names, id) => {
                    const country = countries?.find((c) => c.id === id);

                    if (typeof country !== "undefined") {
                        return names.concat(country.text);
                    }

                    return names;
                }, [])
                .join(", "),
        [countries],
    );

    const tooltipText = selectedIds.length > 1 ? getCountriesNames(selectedIds) : undefined;

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (open && isDisabled) {
                return;
            }
            setIsOpen(open);
            if (open) {
                onOpen?.();
            } else {
                setSearchTerm("");
            }
        },
        [isDisabled, onOpen],
    );

    const handleClearSearchTerm = useCallback(() => {
        setSearchTerm("");
    }, []);

    const toggleExpanded = useCallback((regionName: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(regionName)) {
                next.delete(regionName);
            } else {
                next.add(regionName);
            }
            return next;
        });
    }, []);

    const handleClose = useCallback(
        (event: React.SyntheticEvent) => {
            onSelect([]);
            event.stopPropagation();
        },
        [onSelect],
    );

    const handleChange = useCallback(
        (ids: readonly number[]) => {
            onSelect(ids);
        },
        [onSelect],
    );

    return (
        <Box
            position="relative"
            id="employee-location-dropdown-container"
            data-automation={`${DATA_AUTOMATION}-container`}
        >
            <Combobox
                isOpen={isOpen}
                onOpenChange={handleOpenChange}
                placement="bottom-start"
                width="300px"
            >
                <Combobox.Trigger>
                    <Flex inline>
                        <CountryDropdownButton
                            isDisabled={isDisabled}
                            selectedIds={selectedIds}
                            buttonText={buttonText}
                            tooltipText={tooltipText}
                            onCloseItem={handleClose}
                        />
                    </Flex>
                </Combobox.Trigger>

                <Combobox.SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder={translate("si.lead_gen_filters.employeeLocation.dd_placeholder")}
                />

                {searchTerm.length > 0 ? (
                    <Flex
                        position="absolute"
                        top="16px"
                        right="10px"
                        zIndex={1}
                        alignItems="center"
                    >
                        <IconButton
                            type="flat"
                            iconName="clear"
                            iconSize="xs"
                            aria-label={translate("common.clear")}
                            dataAutomation={`${DATA_AUTOMATION}-search-clear`}
                            onClick={handleClearSearchTerm}
                        />
                    </Flex>
                ) : null}

                <DropdownHeader />

                <Combobox.Content
                    emptyState={<Combobox.OptionLabel>{translate("dropdown.no.results")}</Combobox.OptionLabel>}
                >
                    {visibleGroups.map(({ group, filteredCountries, filteredSubGroups }) => (
                        <EmployeeLocationRegionSection
                            key={group.region.name}
                            group={group}
                            filteredCountries={filteredCountries}
                            filteredSubGroups={filteredSubGroups}
                            selectedCodes={selectedCodes}
                            dataAutomation={DATA_AUTOMATION}
                            searchActive={searchActive}
                            isRegionExpanded={isRegionExpanded}
                            onChange={handleChange}
                            toggleExpanded={toggleExpanded}
                        />
                    ))}
                </Combobox.Content>
            </Combobox>
        </Box>
    );
};

export default EmployeeLocationDropdown;
